import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { invitesApi } from "../../api";
import { useMutation } from "../../hooks/useExclusiveMutation";
import { queryKeys } from "../../queryKeys";
import type { InvitePayInfo } from "../../types/invite";
import { InviteBrand, useInviteTheme } from "./inviteTheme";
import "./PublicInvitePage.css";

function formatHold(iso?: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "numeric",
    minute: "2-digit",
    day: "numeric",
    month: "short",
  });
}

/** Personal pass link sent on approval. Opening it never starts a checkout (link previews
 *  would); the guest taps Pay, we open a Ticketing checkout and hand over to PayU. */
const InvitePayPage: React.FC = () => {
  const { payToken } = useParams<{ payToken: string }>();
  const [error, setError] = useState("");

  const infoQuery = useQuery<InvitePayInfo>({
    queryKey: payToken ? queryKeys.publicInvitePay(payToken) : ["invites", "pay", "idle"],
    queryFn: () => invitesApi.payInfo(payToken!),
    enabled: Boolean(payToken),
    retry: 1,
  });
  const info: InvitePayInfo | undefined = infoQuery.data;
  useInviteTheme(info?.theme ?? "neon", info ? `Your pass · ${info.event_name}` : undefined);

  const pay = useMutation({
    mutationFn: () => invitesApi.startPayment(payToken!),
    onSuccess: (res) => {
      if (res.status === "payable" && res.pay_url) {
        window.location.assign(res.pay_url);
        return;
      }
      void infoQuery.refetch();
    },
    onError: (err: unknown) => setError(err instanceof Error ? err.message : "Something went wrong. Please try again."),
  });

  const theme = info?.theme ?? "neon";
  let title = "Link not found";
  let body: React.ReactNode = <p>This pass link is invalid. Please check the message we sent you.</p>;

  if (info) {
    if (info.status === "payable") {
      title = `You're on the list, ${info.first_name}`;
      body = (
        <>
          <p>
            Your pass for {info.event_name} is held for you until <strong>{formatHold(info.hold_expires_at)}</strong>.
          </p>
          <button className="inv-submit" type="button" disabled={pay.isPending} onClick={() => pay.mutate()}>
            {pay.isPending ? "Opening payment…" : "Pay and confirm"}
          </button>
          <p className="inv-form-error" role="alert">{error}</p>
        </>
      );
    } else if (info.status === "paid") {
      title = `You're in, ${info.first_name}`;
      body = (
        <>
          <p>Your pass ID is {info.application_no}. We'll send the venue and your arrival time before the event.</p>
          {info.status_url ? (
            <a className="inv-submit" href={info.status_url}>
              View your pass
            </a>
          ) : null}
        </>
      );
    } else if (info.status === "expired") {
      title = "This link has expired";
      body = <p>Your hold ran out, so the spot went back to the waitlist. We'll reach out if one opens again.</p>;
    } else {
      title = "This link isn't active";
      body = <p>We'll reach out on WhatsApp if a spot opens.</p>;
    }
  }

  return (
    <div className={`inv inv--${theme}`}>
      <div className="inv-status-page">
        {info ? <InviteBrand brand={info.brand} /> : null}
        {infoQuery.isLoading ? null : (
          <section className="inv-done" aria-live="polite">
            <div className="inv-rule" aria-hidden="true">
              <span />
              <i />
              <span />
            </div>
            <h2>{title}</h2>
            {body}
            {info ? <span className="inv-ref">Request {info.application_no}</span> : null}
          </section>
        )}
      </div>
    </div>
  );
};

export default InvitePayPage;
